import { Field, Input, Label } from "@headlessui/react";
import clsx from "clsx";
import { useState } from "react";

/**
 * Props interface for the LabelField component.
 */
interface LabelFieldProps extends React.ComponentProps<typeof Input> {
  /** The label text to display above the input field */
  label: string;
  /** Optional error message to display above the input */
  error?: string | undefined;

  /** The type of the input field */
  type: string;

  /** Optional ref to forward to the input element */
  ref?: React.Ref<HTMLElement>;

  /** The name of the input field */
  name: string; // added by register method from react-hook-form
}

/**
 * A floating label input field component that provides a modern, accessible input experience.
 *
 * Features:
 * - Floating label that animates when focused or filled
 * - Error state handling with visual feedback
 * - Proper accessibility with Headless UI components
 * - Responsive design with Tailwind CSS
 *
 * @example
 * ```tsx
 * <LabelField
 *   label="Email Address"
 *   type="email"
 *   error="Please enter a valid email"
 *   onChange={(e) => setEmail(e.target.value)}
 * />
 * ```
 */
export default function LabelField({ label, error, ref, className, ...props }: LabelFieldProps) {
  /** Tracks whether the input field is currently focused */
  const [focused, setFocused] = useState(false);
  /** Tracks whether the input field has a value */
  const [hasValue, setHasValue] = useState(false);

  /**
   * Handles the focus event on the input field.
   * Sets the focused state to true to trigger label animation.
   */
  const handleFocus = () => setFocused(true);

  /**
   * Handles the blur event on the input field.
   * Sets the focused state to false and updates the hasValue state.
   *
   * @param event - The blur event object
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setHasValue(event.target.value !== "");
  };

  /**
   * Handles the change event on the input field.
   * Updates the hasValue state and calls the optional onChange prop.
   *
   * @param event - The change event object
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(event.target.value !== "");
    props.onChange?.(event);
  };

  return (
    <Field className="relative">
      {/* Error message with proper spacing */}
      {error && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 font-medium" data-testid="field-error">
            {error}
          </p>
        </div>
      )}
      <div className="relative">
        <Input
          {...props}
          {...(ref && { ref })}
          className={clsx(
            "peer w-full px-3 pt-5 pb-2 border-2 rounded-xl bg-white text-gray-600 font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-base",
            {
              "border-red-500": error,
              "border-black": !error,
            },
            className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
        />
        <Label
          className={clsx("absolute left-3 transition-all duration-200 pointer-events-none font-mono", {
            "top-1.5 text-xs text-pink-600 font-semibold": focused || hasValue,
            "top-1/2 -translate-y-1/2 text-base text-gray-500 font-semibold": !focused && !hasValue,
          })}
          data-testid="field-label"
        >
          {label}
        </Label>
      </div>
    </Field>
  );
}
